import { useContext, useMemo, useState } from 'react'
import { Listbox, Switch, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { equipmentOptions } from 'functions/equipmentUtils'
import { ModalContext } from '../ModalContext'

import ExerciseImage from 'components/ExerciseImage'

function isJsonObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function parseJsonObject(value) {
    if (isJsonObject(value)) return value

    if (typeof value === 'string' && value.trim().length > 0) {
        try {
            const parsed = JSON.parse(value)
            return isJsonObject(parsed) ? parsed : null
        } catch {
            return null
        }
    }

    return null
}

function formatOptionLabel(optionKey) {
    if (typeof optionKey !== 'string' || optionKey.length === 0) return 'Assessment'

    return optionKey.charAt(0).toUpperCase() + optionKey.slice(1)
}

function getAssessmentOptions(assessmentTemplatesJSON) {
    if (!isJsonObject(assessmentTemplatesJSON)) return []

    if (Array.isArray(assessmentTemplatesJSON.options)) {
        return assessmentTemplatesJSON.options.filter((optionKey) => (
            typeof optionKey === 'string'
            && optionKey.length > 0
            && isJsonObject(assessmentTemplatesJSON[optionKey])
        ))
    }

    return Object.keys(assessmentTemplatesJSON).filter((key) => (
        isJsonObject(assessmentTemplatesJSON[key]) && Array.isArray(assessmentTemplatesJSON[key].phases)
    ))
}

function cloneAssessmentTemplate(template) {
    if (!isJsonObject(template)) return null

    const assessment = JSON.parse(JSON.stringify(template))

    ;(assessment.phases ?? []).forEach((phase) => {
        ;(phase.slots ?? []).forEach((slot) => {
            ;(slot.sets ?? []).forEach((set) => {
                const assigned = Number(set?.setUnitsAssigned) || 0

                if (isUnilateralSet(set)) {
                    set.leftSetUnitsCompleted = assigned
                    set.rightSetUnitsCompleted = assigned
                    return
                }

                if (Object.prototype.hasOwnProperty.call(set, 'setUnitsCompleted')) {
                    set.setUnitsCompleted = assigned
                }
            })
        })
    })

    return assessment
}

function inferOptionKey(assessmentTemplatesJSON, assessment) {
    const options = getAssessmentOptions(assessmentTemplatesJSON)

    if (!isJsonObject(assessment) || options.length === 0) return null

    const byName = options.find((optionKey) => assessmentTemplatesJSON[optionKey]?.name === assessment.name)

    if (byName) return byName

    const assessmentSlotId = assessment.phases?.[0]?.slots?.[0]?.assessmentSlotId

    if (!assessmentSlotId) return options[0] ?? null

    return options.find((optionKey) => (
        assessmentTemplatesJSON[optionKey]?.phases?.[0]?.slots?.[0]?.assessmentSlotId === assessmentSlotId
    )) ?? options[0] ?? null
}

function isUnilateralSet(set) {
    if (!isJsonObject(set)) return false

    return Object.prototype.hasOwnProperty.call(set, 'leftSetUnitsCompleted')
        || Object.prototype.hasOwnProperty.call(set, 'rightSetUnitsCompleted')
}

function setCompletion(set) {
    const assigned = Number(set?.setUnitsAssigned) || 0

    if (isUnilateralSet(set)) {
        const max = assigned * 2
        const user = (Number(set.leftSetUnitsCompleted) || 0) + (Number(set.rightSetUnitsCompleted) || 0)

        return {
            user,
            max,
            percent: max > 0 ? (user / max) * 100 : 0
        }
    }

    const user = Number(set?.setUnitsCompleted) || 0

    return {
        user,
        max: assigned,
        percent: assigned > 0 ? (user / assigned) * 100 : 0
    }
}

function assessmentCompletion(assessment) {
    const totals = { user: 0, max: 0 }

    ;(assessment?.phases ?? []).forEach((phase) => {
        ;(phase?.slots ?? []).forEach((slot) => {
            ;(slot?.sets ?? []).forEach((set) => {
                const completion = setCompletion(set)
                totals.user += completion.user
                totals.max += completion.max
            })
        })
    })

    return {
        ...totals,
        percent: totals.max > 0 ? (totals.user / totals.max) * 100 : 0
    }
}

function slotCompletion(slot) {
    const totals = { user: 0, max: 0 }

    ;(slot?.sets ?? []).forEach((set) => {
        const completion = setCompletion(set)
        totals.user += completion.user
        totals.max += completion.max
    })

    return {
        ...totals,
        percent: totals.max > 0 ? (totals.user / totals.max) * 100 : 0
    }
}

function formatPercent(percent) {
    return `${Math.round(percent)}%`
}

function scoreTextClass(percent) {
    return percent < 75 ? 'text-red-500' : 'text-green-600'
}

function getEquipmentIcons(exercise) {
    if (!exercise?.equipment) return []

    return exercise.equipment
        .split(',')
        .map((key) => equipmentOptions.find((option) => option.key === key.trim()))
        .filter(Boolean)
}

function formatSetDetails(set, exercise) {
    const setUnit = exercise?.setUnit || 'reps'
    const assigned = set?.setUnitsAssigned ?? 0
    const unitsLabel = `${assigned} ${setUnit}${isUnilateralSet(set) ? ' / side' : ''}`

    const weight = set?.labelWeightRecommendationRounded ?? set?.weightRecommendationRounded ?? 0
    let loadLabel = ''

    if (weight > 0) {
        loadLabel = `${weight} lbs`
    } else if (set?.formulaTag === 'bodyweight' || exercise?.exerciseType === 'bodyweight') {
        loadLabel = 'Bodyweight'
    } else if (set?.formulaTag) {
        loadLabel = set.formulaTag
    }

    return { unitsLabel, loadLabel }
}

function AssessmentOptionDropdown({
    options = [],
    selectedOptionKey,
    onChange
}) {
    return (
        <Listbox
            value={selectedOptionKey}
            onChange={onChange}
        >
            <div className="relative w-full">
                <Listbox.Button className="relative flex h-9 w-full cursor-pointer items-center rounded-md border border-gray-300 bg-white pl-3 pr-8 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400">
                    <span className="block truncate">
                        {selectedOptionKey ? formatOptionLabel(selectedOptionKey) : 'Select option'}
                    </span>

                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                    </span>
                </Listbox.Button>

                <Transition
                    as="div"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                        {options.map((optionKey) => (
                            <Listbox.Option
                                key={optionKey}
                                value={optionKey}
                                className={({ active }) => `
                                    relative cursor-pointer select-none py-2 pl-3 pr-8 text-left
                                    ${active ? 'bg-orange-50' : ''}
                                `}
                            >
                                {({ selected }) => (
                                    <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {formatOptionLabel(optionKey)}
                                        </span>

                                        {selected &&
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-orange-600">
                                                <CheckIcon className="h-5 w-5" />
                                            </span>
                                        }
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    )
}

function UnitSlider({
    label,
    value,
    max,
    onChange
}) {
    return (
        <div className="flex min-w-0 flex-1 items-center gap-x-1.5">
            {label && <p className="shrink-0 text-left text-xs text-gray-500">{label}</p>}

            <input
                type="range"
                min={0}
                max={Math.max(max, 0)}
                value={Math.min(Math.max(value, 0), Math.max(max, 0))}
                onChange={(event) => onChange(parseInt(event.target.value, 10) || 0)}
                className="h-2 min-w-0 flex-1 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-orange-400"
            />

            <p className="w-5 shrink-0 text-center text-xs">{value}</p>
        </div>
    )
}

export default function AssessmentInputModal() {
    const { modalData } = useContext(ModalContext)
    const { exerciseHashMap, exercises } = useGetExercises()

    const programMetadata = modalData?.programMetadata ?? null
    const setAssessmentJSON = modalData?.setAssessmentJSON ?? (() => {})
    const assessmentTemplatesJSON = parseJsonObject(programMetadata?.assessmentTemplatesJSON)
    const options = useMemo(() => getAssessmentOptions(assessmentTemplatesJSON), [assessmentTemplatesJSON])

    const [assessment, setAssessment] = useState(() => (
        isJsonObject(modalData?.assessmentJSON) ? modalData.assessmentJSON : null
    ))
    const [selectedOptionKey, setSelectedOptionKey] = useState(() => (
        inferOptionKey(assessmentTemplatesJSON, modalData?.assessmentJSON) ?? options[0] ?? null
    ))

    const completion = useMemo(() => assessmentCompletion(assessment), [assessment])
    const assessmentActive = assessment !== null
    const exercisesLoading = Object.keys(exerciseHashMap).length === 0 || exercises.length === 0

    const ApplyAssessment = (nextAssessment) => {
        setAssessment(nextAssessment)
        setAssessmentJSON(nextAssessment)
    }

    const ToggleAssessment = () => {
        if (assessment !== null) {
            ApplyAssessment(null)
            return
        }

        const optionKey = options[0] ?? null
        setSelectedOptionKey(optionKey)

        if (optionKey === null) return

        ApplyAssessment(cloneAssessmentTemplate(assessmentTemplatesJSON[optionKey]))
    }

    const SelectOption = (optionKey) => {
        setSelectedOptionKey(optionKey)
        ApplyAssessment(cloneAssessmentTemplate(assessmentTemplatesJSON?.[optionKey]))
    }

    const UpdateCompletedUnits = (phaseIndex, slotIndex, setIndex, field, value) => {
        if (assessment === null) return

        const nextAssessment = JSON.parse(JSON.stringify(assessment))
        nextAssessment.phases[phaseIndex].slots[slotIndex].sets[setIndex][field] = value
        ApplyAssessment(nextAssessment)
    }

    return (
        <div className="pointer-events-none absolute inset-0 flex flex-col p-4 pt-8 text-left">
            <div className="pointer-events-auto shrink-0 space-y-3">
                <div
                    className="flex cursor-pointer items-center justify-between rounded-md border border-gray-300 px-3 py-2"
                    onClick={() => ToggleAssessment()}
                >
                    <p className="text-sm text-gray-700">Assessment input</p>

                    <Switch
                        checked={assessmentActive}
                        className={`
                            relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                            ${assessmentActive ? 'bg-orange-600' : 'bg-gray-200'}
                        `}
                    >
                        <span className="sr-only">Use assessment</span>
                        <span
                            aria-hidden="true"
                            className={`
                                pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                ${assessmentActive ? 'translate-x-4' : 'translate-x-0'}
                            `}
                        />
                    </Switch>
                </div>

                {assessmentActive && options.length > 1 && (
                    <AssessmentOptionDropdown
                        options={options}
                        selectedOptionKey={selectedOptionKey}
                        onChange={SelectOption}
                    />
                )}

                {options.length === 0 && (
                    <p className="text-sm text-gray-500">This program has no assessment templates.</p>
                )}

                {assessmentActive && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-x-3">
                            <p className="min-w-0 flex-1 truncate text-xl font-semibold leading-6 text-gray-900">
                                {assessment?.name || 'Assessment'}
                            </p>

                            <p className={`shrink-0 text-sm font-semibold ${scoreTextClass(completion.percent)}`}>
                                {formatPercent(completion.percent)}
                            </p>
                        </div>

                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="h-full rounded-full bg-orange-500"
                                style={{ width: `${Math.min(Math.max(completion.percent, 0), 100)}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {assessmentActive && (
                <div className="pointer-events-auto mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {exercisesLoading && (
                        <>
                            <div className="h-20 w-full animate-pulse rounded-lg bg-gray-200" />
                            <div className="h-20 w-full animate-pulse rounded-lg bg-gray-200" />
                            <div className="h-20 w-full animate-pulse rounded-lg bg-gray-200" />
                        </>
                    )}

                    {!exercisesLoading && (assessment.phases ?? []).map((phase, phaseIndex) => (
                        <div
                            key={phaseIndex}
                            className="divide-y divide-gray-400 overflow-hidden rounded-lg border border-gray-400"
                        >
                            <div className="flex items-center gap-x-2 bg-gray-100 p-2">
                                <p className="pl-1 text-left text-lg font-bold">{phase.name}</p>
                                <div className="flex-1" />
                                <p className="text-sm text-gray-600">Sets: {phase.sets ?? 1}</p>
                            </div>

                            {(phase.slots ?? []).map((slot, slotIndex) => {
                                const exercise = exerciseHashMap[slot.exerciseId] ?? null
                                const equipmentIcons = getEquipmentIcons(exercise)
                                const sets = slot.sets ?? []
                                const completionForSlot = slotCompletion(slot)
                                const firstSet = sets[0] ?? null
                                const { unitsLabel, loadLabel } = formatSetDetails(firstSet, exercise)

                                return (
                                    <div
                                        key={slot.assessmentSlotId ?? slotIndex}
                                        className="flex items-start gap-x-3 p-2"
                                    >
                                        <ExerciseImage
                                            exercise={exercise ?? { id: slot.exerciseId, name: `Exercise ${slot.exerciseId}` }}
                                            exercises={exercises}
                                        />

                                        <div className="min-w-0 flex-1 space-y-1">
                                            <div className="flex items-center gap-x-1.5">
                                                <p className="min-w-0 truncate text-left text-sm font-medium text-gray-900">
                                                    {exercise?.name || `Exercise ${slot.exerciseId}`}
                                                </p>

                                                <p className="shrink-0 text-xs text-gray-500">
                                                    {unitsLabel}{loadLabel ? ` · ${loadLabel}` : ''}
                                                </p>

                                                {equipmentIcons.length > 0 && (
                                                    <div className="flex shrink-0 items-center gap-x-1">
                                                        {equipmentIcons.map((equipment) => (
                                                            <img
                                                                key={equipment.key}
                                                                src={equipment.imageURL}
                                                                alt={equipment.name || 'Equipment'}
                                                                className="h-4"
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {sets.map((set, setIndex) => {
                                                const unilateral = isUnilateralSet(set)
                                                const assigned = Number(set?.setUnitsAssigned) || 0

                                                return (
                                                    <div key={setIndex} className="space-y-1">
                                                        {sets.length > 1 && (
                                                            <p className="text-left text-xs text-gray-400">Set {setIndex + 1}</p>
                                                        )}

                                                        <div className="flex items-center gap-x-3">
                                                            {unilateral && (
                                                                <>
                                                                    <UnitSlider
                                                                        label="L"
                                                                        value={Number(set.leftSetUnitsCompleted) || 0}
                                                                        max={assigned}
                                                                        onChange={(value) => UpdateCompletedUnits(phaseIndex, slotIndex, setIndex, 'leftSetUnitsCompleted', value)}
                                                                    />
                                                                    <UnitSlider
                                                                        label="R"
                                                                        value={Number(set.rightSetUnitsCompleted) || 0}
                                                                        max={assigned}
                                                                        onChange={(value) => UpdateCompletedUnits(phaseIndex, slotIndex, setIndex, 'rightSetUnitsCompleted', value)}
                                                                    />
                                                                </>
                                                            )}

                                                            {!unilateral && (
                                                                <UnitSlider
                                                                    value={Number(set.setUnitsCompleted) || 0}
                                                                    max={assigned}
                                                                    onChange={(value) => UpdateCompletedUnits(phaseIndex, slotIndex, setIndex, 'setUnitsCompleted', value)}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>

                                        <p className={`w-12 shrink-0 pt-1 text-right text-sm font-semibold ${scoreTextClass(completionForSlot.percent)}`}>
                                            {formatPercent(completionForSlot.percent)}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
